import { useState } from 'react';
import { useQuery } from 'react-query';
import { Package, Search, Truck } from 'lucide-react';
import { inventoryLocationsAPI } from '@shared/apiClient';

export default function MyTruckStock() {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading } = useQuery(
    ['myTruckStock'],
    () => inventoryLocationsAPI.getMyTruckStock()
  );

  const location = data?.data?.location;
  const stockRows = data?.data?.stock || [];

  const filteredStock = stockRows.filter((row) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const item = row.inventory;
    return (
      item?.itemNumber?.toLowerCase().includes(term)
      || item?.description?.toLowerCase().includes(term)
      || item?.manufacturerPartNumber?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">My Truck Stock</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          {location ? `Parts currently on ${location.name}` : 'Your truck inventory location'}
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search parts on your truck..."
          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading truck stock...</div>
        ) : filteredStock.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {stockRows.length === 0
              ? 'No parts on your truck yet. Stock is added when parts are transferred from the warehouse.'
              : 'No parts match your search.'}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filteredStock.map((row) => {
              const item = row.inventory;
              const qty = parseFloat(row.quantityOnHand);
              const isNegative = qty < 0;
              return (
                <li key={row.id} className="px-4 py-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-900">{item?.itemNumber}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600 truncate">{item?.description}</p>
                    {item?.manufacturerPartNumber && (
                      <p className="mt-0.5 text-xs text-slate-400">MPN: {item.manufacturerPartNumber}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-lg font-semibold ${isNegative ? 'text-red-600' : 'text-slate-900'}`}>
                      {qty.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">on truck</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
        Parts selected from inventory on a job automatically deduct from your truck stock.
        If you&apos;re running low, submit a Parts Needed request from the assistant or clients page.
      </div>
    </div>
  );
}
